/**
 * Generic confirm dialog built on {@link BaseModal} (danger / info presets).
 */

import * as React from 'react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';
import { AlertTriangle, Info, type LucideIcon } from 'lucide-react';

/** Props for {@link ConfirmModal}. */
interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'validation' | 'warning' | 'info' | 'primary';
  /** Replaces the variant header icon when set. */
  icon?: LucideIcon;
  isLoading?: boolean;
  confirmDisabled?: boolean;
  children?: React.ReactNode;
}

/** Confirmation dialog built on {@link BaseModal}. */
export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirmer',
  cancelText = 'Annuler',
  variant = 'info',
  icon,
  isLoading = false,
  confirmDisabled = false,
  children,
}) => {
  const [isProcessing, setIsProcessing] = React.useState(false);

  /** Await `onConfirm` then close; keeps the footer in a processing state. */
  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setIsProcessing(false);
    }
  };

  /** Danger / validation use {@link BaseModal} header presets unless a custom icon is passed. */
  const headerPreset =
    !icon && (variant === 'danger' || variant === 'validation') ? variant : undefined;
  const icons = {
    warning: AlertTriangle,
    info: Info,
    primary: Info,
  };
  const iconColors = {
    warning: 'var(--color-3-nuance-1)',
    info: 'var(--color-2-nuance-1)',
    primary: 'var(--color-1-nuance-1)',
    danger: 'rgb(var(--color-error))',
    validation: 'rgb(var(--color-success))',
  };
  const iconBgColors = {
    warning: 'color-mix(in srgb, var(--color-3-nuance-1) 15%, transparent)',
    info: 'color-mix(in srgb, var(--color-2-nuance-1) 15%, transparent)',
    primary: 'color-mix(in srgb, var(--color-1-nuance-1) 15%, transparent)',
    danger: 'rgb(var(--color-error) / 0.1)',
    validation: 'rgb(var(--color-success) / 0.1)',
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      size="md"
      header={{
        preset: headerPreset,
        icon: headerPreset ? undefined : (icon ?? icons[variant]),
        iconColor: headerPreset ? undefined : iconColors[variant],
        iconBgColor: headerPreset ? undefined : iconBgColors[variant],
        iconSize: 'md',
        title,
        titleSize: 'lg',
        titleWeight: 'semibold',
      }}
      description={description}
      hideContentWrapper={true}
      footer={
        <div className="flex gap-2 justify-end w-full">
          <Button preset="modal-cancel" onClick={onClose} disabled={isProcessing || isLoading}>
            {cancelText}
          </Button>
          <Button
            preset={variant === 'danger' ? 'modal-danger' : 'modal-confirm'}
            onClick={handleConfirm}
            loading={isProcessing || isLoading}
            disabled={isProcessing || isLoading || confirmDisabled}
          >
            {confirmText}
          </Button>
        </div>
      }
    >
      {children ?? <div />}
    </BaseModal>
  );
};
